/**
 * Hearing when the candidate has finished an answer, for hands-free practice.
 *
 * Google's own end-of-speech detection is switched off for these sessions
 * (`audio/live.py` locks `automaticActivityDetection.disabled` into the token),
 * because a pause to think must never end an answer early. So the decision is
 * made here, in the page, from how loud each 100 ms chunk of microphone audio
 * is - nothing extra is sent anywhere.
 *
 * The rule, in words: the answer has started once the candidate has been
 * speaking for 0.3 s; it is over once they have then been quiet for
 * `silenceMs` (3 s by default - long enough to think mid-sentence). "Quiet" is
 * judged against the room: the detector keeps a running estimate of the
 * background noise while nobody is speaking, so a fan or a busy café does not
 * count as talking, and a soft voice in a silent room still does. The first
 * 0.4 s of listening only measure the room: that is the moment the question
 * has just ended, before anyone starts to answer, and without it a constant
 * noise heard first would be mistaken for the start of an answer that then
 * never ends.
 *
 * Pure and synchronous - no audio APIs - so the rule can be checked on its own.
 */

export type SilenceState = "waiting" | "speaking" | "pausing" | "done" | "no-speech"

export interface SilenceOptions {
  /** Length of each chunk pushed in, in milliseconds. */
  chunkMs?: number
  /** Quiet this long after speech ends the answer. */
  silenceMs?: number
  /** Speech this long before the answer counts as started. */
  startMs?: number
  /** Give up if nothing is said for this long. */
  noSpeechMs?: number
  /** The first this-long of audio only measures the room's background noise. */
  calibrateMs?: number
}

export interface SilenceDetector {
  /** Feed one chunk of 16-bit PCM; returns the state after it. */
  push: (chunk: Int16Array) => SilenceState
  /** Loudness of the last chunk, 0 to 1 - for a level meter. */
  level: () => number
}

/** The quietest level ever treated as speech, whatever the room. */
const MIN_THRESHOLD = 0.012
/** Speech must be this many times louder than the background. */
const OVER_NOISE = 2.5
/** How fast the background estimate follows the room (per chunk). */
const NOISE_ADAPT = 0.05
/**
 * The loudest a room is assumed to be. Someone who starts answering during
 * the 0.4 s of measuring would otherwise set the bar at their own voice.
 */
const MAX_NOISE = 0.02

/** Root-mean-square loudness of a chunk, 0 (silence) to 1 (full scale). */
export function loudness(chunk: Int16Array): number {
  if (chunk.length === 0) return 0
  let sum = 0
  for (let i = 0; i < chunk.length; i++) {
    const sample = chunk[i] / 32768
    sum += sample * sample
  }
  return Math.sqrt(sum / chunk.length)
}

export function createSilenceDetector(options: SilenceOptions = {}): SilenceDetector {
  const chunkMs = options.chunkMs ?? 100
  const silenceChunks = Math.ceil((options.silenceMs ?? 3000) / chunkMs)
  const startChunks = Math.ceil((options.startMs ?? 300) / chunkMs)
  const noSpeechChunks = Math.ceil((options.noSpeechMs ?? 20000) / chunkMs)
  const calibrateChunks = Math.ceil((options.calibrateMs ?? 400) / chunkMs)

  let state: SilenceState = "waiting"
  let noise = MIN_THRESHOLD / OVER_NOISE
  let loudRun = 0
  let quietRun = 0
  let waited = 0
  let last = 0
  let measured = 0

  return {
    level: () => last,
    push(chunk) {
      if (state === "done" || state === "no-speech") return state
      last = loudness(chunk)
      if (waited < calibrateChunks) {
        waited += 1
        measured += last
        if (waited === calibrateChunks) noise = Math.min(MAX_NOISE, measured / calibrateChunks)
        return state
      }
      const threshold = Math.max(MIN_THRESHOLD, noise * OVER_NOISE)
      const loud = last > threshold

      if (state === "waiting") {
        waited += 1
        if (loud) {
          loudRun += 1
          if (loudRun >= startChunks) {
            state = "speaking"
            quietRun = 0
          }
        } else {
          loudRun = 0
          // Only quiet chunks teach the detector what the room sounds like.
          noise += (last - noise) * NOISE_ADAPT
          if (waited >= noSpeechChunks) state = "no-speech"
        }
        return state
      }

      if (loud) {
        quietRun = 0
        state = "speaking"
      } else {
        quietRun += 1
        noise += (last - noise) * NOISE_ADAPT
        state = quietRun >= silenceChunks ? "done" : "pausing"
      }
      return state
    },
  }
}
